import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button, useNotification } from '../common';

const ReminderSettings = () => {
  const { showNotification } = useNotification();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Course reminder settings
  const [courseReminderSettings, setCourseReminderSettings] = useState({
    enabled: true,
    sendToTutor: true,
    sendToStudent: true,
    hoursBeforeClass: 24
  });

  // Invoice reminder settings
  const [invoiceReminderSettings, setInvoiceReminderSettings] = useState({
    enabled: true,
    daysAfterDueDate: 1,
    repeatEveryDays: 3,
    maxReminders: 3
  });

  // Templates data
  const [templates, setTemplates] = useState({
    courseStudent: null,
    courseTutor: null,
    invoice: null
  });

  // Fetch settings and templates
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const config = { headers: { 'x-auth-token': token } };

        // First get company settings
        const settingsRes = await axios.get('/api/settings/company', config);
        
        // Parse reminder settings if they exist
        if (settingsRes.data && settingsRes.data.reminder_settings) {
          try {
            const reminderSettings = JSON.parse(settingsRes.data.reminder_settings);
            if (reminderSettings.course) {
              setCourseReminderSettings(prev => ({
                ...prev,
                ...reminderSettings.course
              }));
            }
            if (reminderSettings.invoice) {
              setInvoiceReminderSettings(prev => ({
                ...prev,
                ...reminderSettings.invoice
              }));
            }
          } catch (parseError) {
            console.error('Error parsing reminder settings:', parseError);
          }
        }

        // Fetch email templates
        const templateRes = await axios.get('/api/email/templates', config);
        if (templateRes.data) {
          // Find course reminder templates
          const studentTemplate = templateRes.data.find(t => 
            t.template_type === 'course_reminder_student' && t.is_default);
          
          const tutorTemplate = templateRes.data.find(t => 
            t.template_type === 'course_reminder_tutor' && t.is_default);
          
          const invoiceTemplate = templateRes.data.find(t => 
            t.template_type === 'notification_invoice_reminder' && t.is_default);
            
          // Check if we have any country-specific default templates
          const countryStudentTemplate = studentTemplate?.country_code ? studentTemplate : null;
          const countryTutorTemplate = tutorTemplate?.country_code ? tutorTemplate : null;
          const countryInvoiceTemplate = invoiceTemplate?.country_code ? invoiceTemplate : null;

          setTemplates({
            courseStudent: studentTemplate || null,
            courseTutor: tutorTemplate || null,
            invoice: invoiceTemplate || null,
            // Store country info if available
            hasCountrySpecific: !!(countryStudentTemplate || countryTutorTemplate || countryInvoiceTemplate)
          });
        }
      } catch (err) {
        console.error('Error fetching reminder settings:', err);
        setError('Failed to load reminder settings. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Handle form changes for course reminders
  const handleCourseReminderChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCourseReminderSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle form changes for invoice reminders
  const handleInvoiceReminderChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInvoiceReminderSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              type === 'number' ? parseInt(value, 10) : value
    }));
  };

  // Save settings
  const saveSettings = async () => {
    setSaving(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      };

      // Combine settings in the expected format
      const reminderSettings = {
        course: courseReminderSettings,
        invoice: invoiceReminderSettings
      };

      // Log what we're sending for debugging
      console.log('Sending reminder settings data:', reminderSettings);
      const jsonSettings = JSON.stringify(reminderSettings);
      console.log('Stringified settings:', jsonSettings);
      
      // Update company settings - try text endpoint first
      try {
        const requestData = { reminder_settings: jsonSettings };
        console.log('Sending request data:', requestData);
        
        const response = await axios.post('/api/settings/company/text', requestData, config);
        
        console.log('Settings saved successfully:', response.data);
        showNotification('Reminder settings saved successfully', 'success');
      } catch (apiError) {
        console.error('API Error saving settings (text endpoint):', apiError);
        if (apiError.response) {
          console.error('Error response data:', apiError.response.data);
          
          // Try the original endpoint as fallback
          console.log('Trying fallback endpoint...');
          try {
            const fallbackResponse = await axios.post('/api/settings/company', {
              reminder_settings: jsonSettings
            }, config);
            
            console.log('Settings saved successfully via fallback:', fallbackResponse.data);
            showNotification('Reminder settings saved successfully', 'success');
            return; // Exit early on success
          } catch (fallbackError) {
            console.error('Fallback endpoint also failed:', fallbackError);
            // Continue to error handling below with original error details
          }
          
          // Extract error message
          let errorMsg = 'Failed to save settings';
          if (apiError.response.data) {
            errorMsg += apiError.response.data.message ? `: ${apiError.response.data.message}` : '';
            errorMsg += apiError.response.data.details ? ` (${apiError.response.data.details})` : '';
          }
          setError(errorMsg);
        } else if (apiError.request) {
          console.error('Error request:', apiError.request);
          setError('Network error - No response received');
        } else {
          console.error('Error message:', apiError.message);
          setError(`Request error: ${apiError.message}`);
        }
        showNotification('Failed to save settings', 'error');
      }
    } catch (err) {
      console.error('Error in saveSettings function:', err);
      setError('Failed to save settings. Please try again.');
      showNotification('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Handle manual run of reminders
  const runManualReminders = async (type) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { 'x-auth-token': token } };
      
      if (type === 'course') {
        // Call the course reminder endpoint
        const res = await axios.post('/api/calendar/send-reminders', {}, config);
        if (res.data && res.data.success) {
          showNotification(`Course reminders sent: ${res.data.totalSent || 0}`, 'success');
        } else {
          showNotification('Failed to send course reminders', 'error');
        }
      } else if (type === 'invoice') {
        // Call the invoice reminder endpoint
        const res = await axios.post('/api/deals/invoices/send-reminders', {}, config);
        if (res.data && res.data.success) {
          showNotification(`Invoice reminders sent: ${res.data.sent || 0}`, 'success');
        } else {
          showNotification('Failed to send invoice reminders', 'error');
        }
      }
    } catch (err) {
      console.error(`Error running manual ${type} reminders:`, err);
      showNotification(`Error sending ${type} reminders`, 'error');
    }
  };


  if (loading) {
    return (
      <div className="py-4 text-center">
        <svg className="animate-spin h-8 w-8 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="mt-2 text-gray-600">Loading reminder settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Reminder Settings</h3>
        
        <div className="flex space-x-2">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded text-sm">
              {error}
            </div>
          )}
          <Button
            onClick={saveSettings}
            variant="primary"
            isLoading={saving}
            disabled={saving}
          >
            Save Settings
          </Button>
        </div>
      </div>

      {/* Course Reminders Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Course Reminders</h3>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs font-medium rounded ${courseReminderSettings.enabled ? 
                'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {courseReminderSettings.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <Button
                onClick={() => runManualReminders('course')}
                variant="outline"
                size="sm"
                disabled={!courseReminderSettings.enabled}
              >
                Send Reminders Now
              </Button>
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Configure automatic reminders for upcoming classes sent to tutors and students.
          </p>
        </div>

        <div className="px-4 py-5 sm:p-6 bg-gray-50">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-6">
              <div className="flex items-center">
                <input
                  id="enabled"
                  name="enabled"
                  type="checkbox"
                  checked={courseReminderSettings.enabled}
                  onChange={handleCourseReminderChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="enabled" className="ml-2 block text-sm font-medium text-gray-700">
                  Enable automatic course reminders
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                When enabled, reminders will be sent automatically before scheduled classes.
              </p>
            </div>

            <div className="sm:col-span-3">
              <label htmlFor="hoursBeforeClass" className="block text-sm font-medium text-gray-700">
                Hours before class to send reminders
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="hoursBeforeClass"
                  id="hoursBeforeClass"
                  min="1"
                  max="72"
                  value={courseReminderSettings.hoursBeforeClass}
                  onChange={handleCourseReminderChange}
                  disabled={!courseReminderSettings.enabled}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Recommended: 24 hours (1 day) before the class starts.
              </p>
            </div>

            <div className="sm:col-span-3">
              <fieldset disabled={!courseReminderSettings.enabled}>
                <legend className="text-sm font-medium text-gray-700">Recipients</legend>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center">
                    <input
                      id="sendToTutor"
                      name="sendToTutor"
                      type="checkbox"
                      checked={courseReminderSettings.sendToTutor}
                      onChange={handleCourseReminderChange}
                      disabled={!courseReminderSettings.enabled}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:text-gray-400"
                    />
                    <label htmlFor="sendToTutor" className="ml-2 block text-sm text-gray-700">
                      Send to tutors
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      id="sendToStudent"
                      name="sendToStudent"
                      type="checkbox"
                      checked={courseReminderSettings.sendToStudent}
                      onChange={handleCourseReminderChange}
                      disabled={!courseReminderSettings.enabled}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:text-gray-400"
                    />
                    <label htmlFor="sendToStudent" className="ml-2 block text-sm text-gray-700">
                      Send to students
                    </label>
                  </div>
                </div>
              </fieldset>
            </div>

            {/* Email Templates Section */}
            <div className="sm:col-span-6 border-t border-gray-200 pt-4">
              <div className="mb-2">
                <h4 className="text-sm font-medium text-gray-700">Email Templates</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Templates are managed in the Email Templates tab. Country-specific templates will be used based on event location.
                </p>
              </div>

              <div className="bg-white rounded-md border border-gray-200 overflow-hidden divide-y divide-gray-200">
                {/* Student Template */}
                <div className="p-4">
                  <h5 className="text-sm font-medium text-gray-700">Student Reminder Template</h5>
                  <p className="text-xs text-gray-500 mt-1">
                    {templates.courseStudent ? 
                      <>Current default: <span className="font-medium">{templates.courseStudent.name}</span></> : 
                      <>No default template found. System will use fallback template.</>}
                  </p>
                  {templates.courseStudent?.country_code && (
                    <p className="text-xs bg-blue-50 text-blue-600 px-2 py-1 mt-1 inline-block rounded">
                      Country-specific templates will override this default when available
                    </p>
                  )}
                </div>

                {/* Tutor Template */}
                <div className="p-4">
                  <h5 className="text-sm font-medium text-gray-700">Tutor Reminder Template</h5>
                  <p className="text-xs text-gray-500 mt-1">
                    {templates.courseTutor ? 
                      <>Current default: <span className="font-medium">{templates.courseTutor.name}</span></> : 
                      <>No default template found. System will use fallback template.</>}
                  </p>
                  {templates.courseTutor?.country_code && (
                    <p className="text-xs bg-blue-50 text-blue-600 px-2 py-1 mt-1 inline-block rounded">
                      Country-specific templates will override this default when available
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Reminders Section */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Invoice Reminders</h3>
            <div className="flex items-center space-x-2">
              <span className={`px-2 py-1 text-xs font-medium rounded ${invoiceReminderSettings.enabled ? 
                'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                {invoiceReminderSettings.enabled ? 'Enabled' : 'Disabled'}
              </span>
              <Button
                onClick={() => runManualReminders('invoice')}
                variant="outline"
                size="sm"
                disabled={!invoiceReminderSettings.enabled}
              >
                Send Reminders Now
              </Button>
            </div>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Configure automatic reminders for overdue invoices sent to clients.
          </p>
        </div>

        <div className="px-4 py-5 sm:p-6 bg-gray-50">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-6">
              <div className="flex items-center">
                <input
                  id="invoiceEnabled"
                  name="enabled"
                  type="checkbox"
                  checked={invoiceReminderSettings.enabled}
                  onChange={handleInvoiceReminderChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="invoiceEnabled" className="ml-2 block text-sm font-medium text-gray-700">
                  Enable automatic invoice reminders
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                When enabled, reminders will be sent automatically for overdue invoices.
              </p>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="daysAfterDueDate" className="block text-sm font-medium text-gray-700">
                Days after due date
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="daysAfterDueDate"
                  id="daysAfterDueDate"
                  min="0"
                  max="30"
                  value={invoiceReminderSettings.daysAfterDueDate}
                  onChange={handleInvoiceReminderChange}
                  disabled={!invoiceReminderSettings.enabled}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Days after due date to send the first reminder.
              </p>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="repeatEveryDays" className="block text-sm font-medium text-gray-700">
                Repeat every (days)
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="repeatEveryDays"
                  id="repeatEveryDays"
                  min="1"
                  max="30"
                  value={invoiceReminderSettings.repeatEveryDays}
                  onChange={handleInvoiceReminderChange}
                  disabled={!invoiceReminderSettings.enabled}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Send follow-up reminders every X days.
              </p>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="maxReminders" className="block text-sm font-medium text-gray-700">
                Maximum reminders
              </label>
              <div className="mt-1">
                <input
                  type="number"
                  name="maxReminders"
                  id="maxReminders"
                  min="1"
                  max="10"
                  value={invoiceReminderSettings.maxReminders}
                  onChange={handleInvoiceReminderChange}
                  disabled={!invoiceReminderSettings.enabled}
                  className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:text-gray-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Maximum number of reminders to send per invoice.
              </p>
            </div>

            {/* Invoice Email Template */}
            <div className="sm:col-span-6 border-t border-gray-200 pt-4">
              <div className="mb-2">
                <h4 className="text-sm font-medium text-gray-700">Email Template</h4>
                <p className="text-xs text-gray-500 mt-1">
                  Templates are managed in the Email Templates tab. Country-specific templates will be used based on recipient's country.
                </p>
              </div>

              <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
                <div className="p-4">
                  <h5 className="text-sm font-medium text-gray-700">Invoice Reminder Template</h5>
                  <p className="text-xs text-gray-500 mt-1">
                    {templates.invoice ? 
                      <>Current default: <span className="font-medium">{templates.invoice.name}</span></> : 
                      <>No default template found. System will use fallback template.</>}
                  </p>
                  {templates.invoice?.country_code && (
                    <p className="text-xs bg-blue-50 text-blue-600 px-2 py-1 mt-1 inline-block rounded">
                      Country-specific templates will override this default when available
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
        <h4 className="text-sm font-medium text-yellow-800 flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          Note about automatic reminders
        </h4>
        <p className="mt-2 text-sm text-yellow-700">
          Reminders are processed automatically once per day. Course reminders are sent before class starts based on the hours you've configured. 
          Invoice reminders are sent after the due date for unpaid invoices. You can also manually trigger reminders using the "Send Reminders Now" button.
        </p>
      </div>
    </div>
  );
};

export default ReminderSettings;